/* ==========================================================================
   IRON CREST LEATHER — scroll-story.js
   One jacket, three scenes, one continuous move.

   Design rules this file obeys:
   · The page is already complete and premium before this script runs. Every
     line below is an upgrade, never a requirement.
   · Only transform and opacity are animated. Nothing here reads layout
     during the scroll loop — geometry is measured once per resize.
   · The pinned sequence is desktop-only, motion-safe and switchable off
     from a single data attribute (Shopify: a theme setting).

   Shopify: assets/scroll-story.js.
     data-animation        → section.settings.enable_animation ("on"/"off")
     data-mobile-animation → section.settings.mobile_animation ("static"/"parallax")
     data-story-height     → section.settings.story_height (vh)
   ========================================================================== */
(function () {
  function boot() {
    'use strict';

    var story = document.getElementById('story');
    var stage = document.getElementById('storyStage');
    if (!story || !stage) return;
    if (story.icBooted) return;   // already live; the editor replaced a different section
    story.icBooted = true;

    var root = document.documentElement;
    var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var sizeQuery = window.matchMedia('(min-width: 900px)');

    var enabled = story.getAttribute('data-animation') !== 'off';
    var mobileMode = story.getAttribute('data-mobile-animation') || 'static';
    var storyHeight = parseInt(story.getAttribute('data-story-height'), 10) || 360;

    /* ---------------------------------------------------------------- maths */
    function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
    function smooth(t) { t = clamp(t); return t * t * t * (t * (t * 6 - 15) + 10); }
    function band(p, a, b) { return clamp((p - a) / (b - a)); }
    function lerp(a, b, t) { return a + (b - a) * t; }

    /* ------------------------------------------------ the travelling jacket */
    // Built in JS: it is a motion artefact, so a no-JS page never sees it.
    var heroImg = stage.querySelector('[data-slot="hero"] img');
    var jacket = null;

    function buildJacket() {
      if (jacket || !heroImg) return;
      jacket = document.createElement('figure');
      jacket.className = 'story__jacket';
      jacket.setAttribute('aria-hidden', 'true');
      var clone = heroImg.cloneNode(false);
      clone.removeAttribute('loading');
      clone.setAttribute('alt', '');
      jacket.appendChild(clone);
      stage.appendChild(jacket);
    }

    /* ------------------------------------------------------------- geometry */
    var keys = [];      // {p, cx, cy, w, rot}
    var baseW = 0, baseH = 0;

    function slotBox(name) {
      var el = stage.querySelector('[data-slot="' + name + '"]');
      if (!el) return null;
      var r = el.getBoundingClientRect();
      var s = stage.getBoundingClientRect();
      return {
        cx: r.left - s.left + r.width / 2,
        cy: r.top - s.top + r.height / 2,
        w: r.width
      };
    }

    function measure() {
      if (!pinned) return;
      var hero = slotBox('hero');
      var detail = slotBox('detail');
      var craft = slotBox('craft');
      if (!hero || !detail || !craft || !hero.w) return;

      baseW = hero.w;
      jacket.style.width = baseW + 'px';
      baseH = baseW * (heroImg.naturalHeight || 1300) / (heroImg.naturalWidth || 1000);

      keys = [
        { p: 0.00, cx: hero.cx,   cy: hero.cy,   w: hero.w,   rot: 0 },
        { p: 0.44, cx: detail.cx, cy: detail.cy, w: detail.w, rot: -2.4 },
        { p: 0.88, cx: craft.cx,  cy: craft.cy,  w: craft.w,  rot: 3.0 }
      ];
    }

    /* ------------------------------------------------------------ the loop */
    var layers = {};
    Array.prototype.forEach.call(stage.querySelectorAll('[data-layer]'), function (el) {
      layers[el.getAttribute('data-layer')] = el;
    });
    var sceneDetail = stage.querySelector('.scene--detail');
    var sceneCraft = stage.querySelector('.scene--craft');

    function setLayer(name, opacity, y) {
      var el = layers[name];
      if (!el) return;
      el.style.opacity = opacity;
      el.style.transform = y ? 'translate3d(0,' + y.toFixed(1) + 'px,0)' : '';
    }

    function setScene(el, visible) {
      if (!el) return;
      el.style.opacity = visible > 0.001 ? 1 : 0;
      el.classList.toggle('is-live', visible > 0.5);
    }

    function paint(p) {
      /* ---- the jacket: one continuous path through three anchors ---- */
      if (keys.length === 3) {
        var a, b, t;
        if (p <= keys[1].p) { a = keys[0]; b = keys[1]; t = smooth(band(p, a.p, b.p)); }
        else { a = keys[1]; b = keys[2]; t = smooth(band(p, a.p, b.p)); }

        var cx = lerp(a.cx, b.cx, t);
        var cy = lerp(a.cy, b.cy, t);
        var w = lerp(a.w, b.w, t);
        var rot = lerp(a.rot, b.rot, t);
        var scale = w / baseW;

        // a slow breath of depth so the move reads as physical, not mechanical
        var drift = Math.sin(p * Math.PI) * 10;

        jacket.style.transform =
          'translate3d(' + (cx - baseW / 2).toFixed(1) + 'px,' +
          (cy - baseH / 2 + drift).toFixed(1) + 'px,0) ' +
          'rotate(' + rot.toFixed(2) + 'deg) scale(' + scale.toFixed(4) + ')';
      }

      /* ---- scene 1 copy recedes, it does not disappear abruptly ---- */
      var heroOut = smooth(band(p, 0.04, 0.24));
      setLayer('hero', 1 - heroOut, heroOut * -64);
      setLayer('cue', 1 - smooth(band(p, 0.01, 0.07)), 0);

      /* ---- scene 2 detail notes ---- */
      var aIn = smooth(band(p, 0.24, 0.36));
      var aOut = smooth(band(p, 0.54, 0.64));
      var bIn = smooth(band(p, 0.28, 0.40));
      var bOut = smooth(band(p, 0.57, 0.67));
      var aV = aIn * (1 - aOut);
      var bV = bIn * (1 - bOut);
      setLayer('detail-a', aV, lerp(28, 0, aIn) + aOut * -28);
      setLayer('detail-b', bV, lerp(34, 0, bIn) + bOut * -28);
      setScene(sceneDetail, Math.max(aV, bV));

      /* ---- scene 3 arrival ---- */
      var cIn = smooth(band(p, 0.72, 0.90));
      setLayer('craft', cIn, lerp(44, 0, cIn));
      setScene(sceneCraft, cIn);

      /* ---- the ground warms as the story resolves ---- */
      var warm = smooth(band(p, 0.55, 0.94));
      var r = Math.round(lerp(255, 247, warm));
      var g = Math.round(lerp(255, 246, warm));
      var b2 = Math.round(lerp(255, 243, warm));
      story.style.setProperty('--page-bg', 'rgb(' + r + ',' + g + ',' + b2 + ')');
    }

    /* -------------------------------------------------------- scroll driver */
    var ticking = false;
    var pinned = null;

    function progress() {
      var total = story.offsetHeight - window.innerHeight;
      if (total <= 0) return 0;
      return clamp(-story.getBoundingClientRect().top / total);
    }

    function update() {
      ticking = false;
      if (!pinned) return;
      paint(progress());
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    /* --------------------------------------------- mobile / fallback motion */
    var parallaxTicking = false;
    function parallax() {
      parallaxTicking = false;
      if (!heroImg) return;
      var r = heroImg.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      var t = clamp((window.innerHeight - r.top) / (window.innerHeight + r.height));
      heroImg.style.transform = 'translate3d(0,' + ((t - 0.5) * -26).toFixed(1) + 'px,0)';
    }
    function onParallaxScroll() {
      if (parallaxTicking) return;
      parallaxTicking = true;
      requestAnimationFrame(parallax);
    }

    /* -------------------------------------------------------------- wiring */
    function teardown() {
      root.classList.remove('has-story');
      story.style.removeProperty('--story-h');
      story.style.removeProperty('--page-bg');
      if (jacket) jacket.style.transform = '';
      ['hero', 'cue', 'detail-a', 'detail-b', 'craft'].forEach(function (n) {
        var el = layers[n];
        if (el) { el.style.opacity = ''; el.style.transform = ''; }
      });
      [sceneDetail, sceneCraft].forEach(function (el) {
        if (el) { el.style.opacity = ''; el.classList.remove('is-live'); }
      });
      window.removeEventListener('scroll', onScroll);
    }

    function setup() {
      var shouldPin = enabled && sizeQuery.matches && !reduceQuery.matches;

      if (shouldPin === pinned) { if (pinned) { measure(); update(); } return; }
      pinned = shouldPin;

      if (!pinned) {
        teardown();
        window.removeEventListener('scroll', onParallaxScroll);
        if (heroImg) heroImg.style.transform = '';
        if (mobileMode === 'parallax' && !reduceQuery.matches) {
          window.addEventListener('scroll', onParallaxScroll, { passive: true });
          parallax();
        }
        return;
      }

      window.removeEventListener('scroll', onParallaxScroll);
      if (heroImg) heroImg.style.transform = '';

      buildJacket();
      root.classList.add('has-story');
      story.style.setProperty('--story-h', storyHeight + 'vh');
      // measure after the class change has been applied to the layout
      requestAnimationFrame(function () { measure(); update(); });
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { setup(); measure(); update(); }, 160);
    });

    if (reduceQuery.addEventListener) reduceQuery.addEventListener('change', setup);
    if (sizeQuery.addEventListener) sizeQuery.addEventListener('change', setup);

    window.addEventListener('load', function () { measure(); update(); });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { measure(); update(); });
    }
    if (heroImg && !heroImg.complete) {
      heroImg.addEventListener('load', function () { measure(); update(); });
    }

    /* ---- "Explore the craft" should land on the arrival, not the prologue ---- */
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href="#craft"]');
      if (!link || !pinned) return;
      e.preventDefault();
      var total = story.offsetHeight - window.innerHeight;
      var top = window.scrollY + story.getBoundingClientRect().top + total * 0.93;
      window.scrollTo({ top: top, behavior: reduceQuery.matches ? 'auto' : 'smooth' });
    });

    setup();
  }

  boot();

  /* The editor replaces a section's markup wholesale on every change, so the
     script has to run again over the new nodes. Storefront visitors never get
     here -- they booted once above. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () { boot(); });
  }
})();
